/**
 * Theme preference: a tri-state the user picks, resolved to a concrete
 * light/dark value the stylesheet keys off (cue-forge-c6b). 'system' follows the
 * OS via prefers-color-scheme; an explicit choice overrides and persists. Dark is
 * the default so a first-run user (or one with no OS preference) matches today.
 *
 * Pure + framework-free (mirrors the cueforge.autosave pref pattern in
 * SessionContext): all storage access is guarded so a locked-down/absent
 * localStorage degrades to the default rather than throwing.
 */

export type ThemePref = 'light' | 'dark' | 'system';

/** The concrete theme the app renders — 'system' is always resolved to one of these. */
export type ResolvedTheme = 'light' | 'dark';

export const THEME_PREF_KEY = 'cueforge.theme';

export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/**
 * Browser-chrome colour per resolved theme (the `--cf-bg` canvas from
 * src/app/styles/tokens.css). Mirrored by the static <meta name="theme-color">
 * twins and the pre-paint script in app/index.html — the parity test guards
 * against drift.
 */
export const THEME_COLORS: Readonly<Record<ResolvedTheme, string>> = {
  dark: '#0e1116',
  light: '#e9e6df',
};

/**
 * How long the provider holds `data-theme-transition` on <html> after a theme
 * flip (drt.22). Must outlast the CSS cross-fade in tokens.css (--cf-transition,
 * 150ms) so the fade isn't cut short, and stay brief so interactions right after
 * a flip don't inherit a sluggish global transition.
 */
export const THEME_TRANSITION_MS = 200;

const DEFAULT_PREF: ThemePref = 'system';

function isThemePref(value: unknown): value is ThemePref {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** Resolve a preference to the concrete theme, given whether the OS prefers dark. */
export function resolveTheme(pref: ThemePref, systemPrefersDark: boolean): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark ? 'dark' : 'light';
  return pref;
}

/** Read the persisted preference (default 'system'); malformed/absent → 'system'. */
export function readThemePref(): ThemePref {
  try {
    const stored = globalThis.localStorage?.getItem(THEME_PREF_KEY);
    return isThemePref(stored) ? stored : DEFAULT_PREF;
  } catch {
    return DEFAULT_PREF;
  }
}

/** Persist the preference; a storage failure is swallowed (never throws). */
export function writeThemePref(pref: ThemePref): void {
  try {
    globalThis.localStorage?.setItem(THEME_PREF_KEY, pref);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Read the OS dark preference. When matchMedia is unavailable or throws
 * (non-browser, or a locked-down environment) we can't detect it, so default to
 * dark — the app's default theme.
 */
export function getSystemPrefersDark(): boolean {
  try {
    return globalThis.matchMedia?.(DARK_SCHEME_QUERY).matches ?? true;
  } catch {
    return true;
  }
}

/**
 * The single source of truth for first-load theme resolution: stored pref
 * (storage failure → 'system') resolved against the OS preference (matchMedia
 * failure → dark). The pre-paint inline script in app/index.html must resolve
 * identically for every input — asserted by prepaint-parity.test.ts. A storage
 * throw must still fall through to matchMedia (locked-down storage on a light
 * OS paints light, not dark).
 */
export function resolveInitialTheme(): ResolvedTheme {
  return resolveTheme(readThemePref(), getSystemPrefersDark());
}

/**
 * Point every <meta name="theme-color"> at the resolved theme's canvas colour so
 * browser chrome (mobile address bar, installed-PWA title bar) follows the app.
 * Overwrites both media-twinned tags from app/index.html, making the explicit
 * choice authoritative over prefers-color-scheme. No-op outside a DOM.
 */
export function applyThemeColorMeta(theme: ResolvedTheme): void {
  const metas = globalThis.document?.querySelectorAll?.('meta[name="theme-color"]');
  if (!metas) return;
  for (const meta of metas) meta.setAttribute('content', THEME_COLORS[theme]);
}
